import { forwardRef, type ReactNode, useCallback, useState } from 'react';
import { Platform, Pressable, type PressableProps, StyleSheet, View } from 'react-native';
import { useFocusVisible } from '../../hooks/useFocusVisible';
import { useHoverCapability } from '../../hooks/useHoverCapability';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { Text } from './Text';

// Tooltip placement: 'above' renders the label above the button, 'below' renders it below.
type TooltipPlacement = 'above' | 'below';

export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonVariant = 'ghost' | 'outline' | 'filled' | 'subtle';

interface IconButtonProps extends Omit<PressableProps, 'children'> {
  icon: ReactNode;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  disabled?: boolean;
  badge?: number;
  tooltip?: string;
  /** Where to render the styled tooltip relative to the button. Default: 'above'. Web only. */
  tooltipPlacement?: TooltipPlacement;
  accessibilityLabel: string;
}

const SIZE_MAP: Record<IconButtonSize, number> = {
  sm: 28,
  md: 36,
  lg: 44,
};

const ICON_AREA_MAP: Record<IconButtonSize, number> = {
  sm: 16,
  md: 20,
  lg: 24,
};

const BADGE_SIZE = 16;
const BADGE_DOT_SIZE = 8;

const getVariantStyles = (
  variant: IconButtonVariant,
  hovered: boolean,
  pressed: boolean,
): {
  backgroundColor: string;
  borderWidth: number;
  borderColor: string;
} => {
  const baseStyles = {
    ghost: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderColor: 'transparent',
    },
    outline: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors.border.muted,
    },
    filled: {
      backgroundColor: colors.primary[500],
      borderWidth: 0,
      borderColor: 'transparent',
    },
    subtle: {
      backgroundColor: colors.primary.muted,
      borderWidth: 0,
      borderColor: 'transparent',
    },
  };

  const result = { ...baseStyles[variant] };

  const hoverBackgrounds: Record<IconButtonVariant, string> = {
    filled: colors.primary[400],
    ghost: colors.interactive.hoverSubtle,
    outline: colors.interactive.hoverSubtle,
    subtle: colors.interactive.hover,
  };

  if (pressed) {
    result.backgroundColor = variant === 'filled' ? colors.primary[600] : colors.interactive.active;
  } else if (hovered) {
    result.backgroundColor = hoverBackgrounds[variant];
  }

  return result;
};

export const IconButton = forwardRef<View, IconButtonProps>(
  (
    {
      icon,
      size = 'md',
      variant = 'ghost',
      disabled = false,
      badge,
      tooltip,
      tooltipPlacement = 'above',
      accessibilityLabel,
      style,
      onBlur,
      onFocus,
      onHoverIn,
      onHoverOut,
      ...rest
    },
    ref,
  ) => {
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    // The ring follows the input modality, as CSS :focus-visible does — a
    // pointer press should not outline what it just pressed, and a keyboard
    // user must still see where they are.
    const focusRingVisible = useFocusVisible();
    const canHover = useHoverCapability();
    const dimension = SIZE_MAP[size];
    const minimumTarget =
      Platform.OS === 'android'
        ? tokens.interaction.minimumTarget.android
        : tokens.interaction.minimumTarget.default;

    const handleHoverIn: NonNullable<PressableProps['onHoverIn']> = useCallback(
      (event) => {
        if (canHover && !disabled) setHovered(true);
        onHoverIn?.(event);
      },
      [canHover, disabled, onHoverIn],
    );
    const handleHoverOut: NonNullable<PressableProps['onHoverOut']> = useCallback(
      (event) => {
        setHovered(false);
        onHoverOut?.(event);
      },
      [onHoverOut],
    );
    const handleFocus: NonNullable<PressableProps['onFocus']> = useCallback(
      (event) => {
        if (Platform.OS === 'web') setFocused(true);
        onFocus?.(event);
      },
      [onFocus],
    );
    const handleBlur: NonNullable<PressableProps['onBlur']> = useCallback(
      (event) => {
        setFocused(false);
        onBlur?.(event);
      },
      [onBlur],
    );

    const hasBadge = badge !== undefined && badge !== null;
    const showCount = hasBadge && badge > 0;
    const badgeLabel = (() => {
      if (!showCount) return undefined;
      return badge > 99 ? '99+' : `${badge}`;
    })();

    const showStyledTooltip =
      Platform.OS === 'web' && ((canHover && hovered) || focused) && !!tooltip;

    const tooltipPositionStyle =
      tooltipPlacement === 'above' ? styles.tooltipAbove : styles.tooltipBelow;

    return (
      <View style={styles.wrapper}>
        <Pressable
          {...rest}
          ref={ref}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          accessibilityState={{ disabled }}
          hitSlop={undefined}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          style={[
            styles.target,
            { minHeight: minimumTarget, minWidth: minimumTarget },
            focused && focusRingVisible && styles.focusVisible,
          ]}
        >
          {({ pressed }) => {
            const variantStyles = getVariantStyles(variant, canHover && hovered, pressed);
            const customStyle = typeof style === 'function' ? style({ pressed }) : style;
            return (
              <View
                pointerEvents="none"
                style={[
                  styles.container,
                  {
                    width: dimension,
                    height: dimension,
                    borderRadius: tokens.radius.md,
                    backgroundColor: variantStyles.backgroundColor,
                    borderWidth: variantStyles.borderWidth,
                    borderColor: variantStyles.borderColor,
                  },
                  customStyle,
                  disabled && styles.disabled,
                ]}
              >
                <View
                  style={[
                    styles.iconContainer,
                    {
                      width: ICON_AREA_MAP[size],
                      height: ICON_AREA_MAP[size],
                    },
                  ]}
                >
                  {icon}
                </View>
                {hasBadge && (
                  <View style={[styles.badge, showCount ? styles.badgeWithCount : styles.badgeDot]}>
                    {showCount && (
                      <Text style={styles.badgeText} color={colors.white}>
                        {badgeLabel}
                      </Text>
                    )}
                  </View>
                )}
              </View>
            );
          }}
        </Pressable>

        {showStyledTooltip && (
          <View
            style={[styles.tooltip, tooltipPositionStyle]}
            // Prevent the tooltip overlay from intercepting pointer events
            pointerEvents="none"
            accessibilityLabel={tooltip}
            role="tooltip"
          >
            <Text style={styles.tooltipText} numberOfLines={1}>
              {tooltip}
            </Text>
          </View>
        )}
      </View>
    );
  },
);

IconButton.displayName = 'IconButton';

const TOOLTIP_OFFSET = 6;

const styles = StyleSheet.create({
  wrapper: {
    // Wraps the Pressable so the absolutely-positioned tooltip escapes
    // the Pressable's own overflow boundary without disrupting layout.
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  target: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.md,
  },
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabled: {
    opacity: 0.4,
  },
  focusVisible:
    Platform.select({
      web: {
        outlineColor: colors.border.focus,
        outlineOffset: tokens.interaction.focusRing.offset,
        outlineStyle: 'solid',
        outlineWidth: tokens.interaction.focusRing.width,
      } as object,
      default: {},
    }) ?? {},
  badge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: colors.status.error.main,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  badgeWithCount: {
    minWidth: BADGE_SIZE,
    height: BADGE_SIZE,
    borderRadius: BADGE_SIZE / 2,
    paddingHorizontal: tokens.spacing.xs,
  },
  badgeDot: {
    width: BADGE_DOT_SIZE,
    height: BADGE_DOT_SIZE,
    borderRadius: BADGE_DOT_SIZE / 2,
  },
  badgeText: {
    fontSize: tokens.fontSize.xs - 1,
    fontWeight: '700',
    lineHeight: BADGE_SIZE - 2,
    textAlign: 'center',
  },
  // Styled hover tooltip — web only. Absolutely positioned so it never
  // affects surrounding layout. pointerEvents="none" on the View prevents
  // it from swallowing mouse events and triggering a flicker loop.
  tooltip: {
    position: 'absolute',
    zIndex: tokens.zIndex.tooltip,
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.sm,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xxs,
    alignSelf: 'center',
    // Keep it above sibling elements on the Z axis
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        } as object)
      : {}),
  },
  tooltipAbove: {
    bottom: '100%',
    marginBottom: TOOLTIP_OFFSET,
  },
  tooltipBelow: {
    top: '100%',
    marginTop: TOOLTIP_OFFSET,
  },
  tooltipText: {
    fontSize: tokens.fontSize.xs,
    color: colors.text.secondary,
    fontWeight: '500',
    lineHeight: 14,
  },
});
